/**
 * OMRTemplateRegistry.ts
 * Defines the layout and coordinates of different optical forms.
 * Coordinates are in pixels relative to a warped image of specified dimensions.
 */

export interface OMRTemplate {
  id: string;
  name: string;
  examType?: string;
  gradeLevel?: string;
  layout: {
    columns: number;
    warpWidth?: number;  // Target width after perspective correction
    warpHeight?: number; // Target height after perspective correction
    sections: OMRSection[];
  };
}

export interface OMRSection {
  name: string;
  startQuestion: number;
  questionCount: number;
  roi: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  grid: {
    rows: number;
    cols: number; // usually 4 or 5 for A-D or A-E
    bubbleWidth: number;
    bubbleHeight: number;
  };
}

/**
 * Registry of known templates.
 */
export const OMR_TEMPLATES: OMRTemplate[] = [
  {
    id: 'yks-optik-129',
    name: 'YKS Standart (TYT 120 + AYT)',
    examType: 'TYT',
    layout: {
      columns: 4,
      warpWidth: 1000,
      warpHeight: 1400,
      sections: [
        {
          name: 'Türkçe',
          startQuestion: 1,
          questionCount: 40,
          roi: { x: 520, y: 280, width: 108, height: 1050 },
          grid: { rows: 40, cols: 5, bubbleWidth: 18, bubbleHeight: 20 }
        },
        {
          name: 'Sosyal Bilimler',
          startQuestion: 1,
          questionCount: 20,
          roi: { x: 635, y: 280, width: 108, height: 540 },
          grid: { rows: 20, cols: 5, bubbleWidth: 18, bubbleHeight: 20 }
        },
        {
          name: 'Temel Matematik',
          startQuestion: 1,
          questionCount: 40,
          roi: { x: 750, y: 280, width: 108, height: 1050 },
          grid: { rows: 40, cols: 5, bubbleWidth: 18, bubbleHeight: 20 }
        },
        {
          name: 'Fen Bilimleri',
          startQuestion: 1,
          questionCount: 20,
          roi: { x: 865, y: 280, width: 108, height: 540 },
          grid: { rows: 20, cols: 5, bubbleWidth: 18, bubbleHeight: 20 }
        }
      ]
    }
  },
  {
    id: 'generic-20',
    name: 'Genel 20 Soruluk Form',
    examType: 'Genel',
    gradeLevel: '5-12',
    layout: {
      columns: 1,
      warpWidth: 800,
      warpHeight: 1100,
      sections: [
        {
          name: 'Sorular',
          startQuestion: 1,
          questionCount: 20,
          roi: { x: 350, y: 200, width: 200, height: 600 },
          grid: { rows: 20, cols: 5, bubbleWidth: 30, bubbleHeight: 22 }
        }
      ]
    }
  },
  {
    id: 'generic-40',
    name: 'Genel 40 Soruluk Form',
    examType: 'Genel',
    layout: {
      columns: 1,
      warpWidth: 800,
      warpHeight: 1200,
      sections: [
        {
          name: 'Sorular',
          startQuestion: 1,
          questionCount: 40,
          roi: { x: 300, y: 180, width: 220, height: 900 },
          grid: { rows: 40, cols: 5, bubbleWidth: 30, bubbleHeight: 18 }
        }
      ]
    }
  },
  {
    id: 'lgs-standard',
    name: 'LGS Formu (90 Soru)',
    examType: 'LGS',
    gradeLevel: 'LGS',
    layout: {
      columns: 2,
      warpWidth: 1000,
      warpHeight: 1400,
      sections: [
        {
          name: 'Sözel',
          startQuestion: 1,
          questionCount: 50,
          roi: { x: 50, y: 200, width: 420, height: 1100 },
          grid: { rows: 50, cols: 4, bubbleWidth: 30, bubbleHeight: 18 }
        },
        {
          name: 'Sayısal',
          startQuestion: 1,
          questionCount: 40,
          roi: { x: 530, y: 200, width: 420, height: 900 },
          grid: { rows: 40, cols: 4, bubbleWidth: 30, bubbleHeight: 18 }
        }
      ]
    }
  }
];

export const getTemplateById = (id: string) => OMR_TEMPLATES.find(t => t.id === id);
